import React from "react";
import { Box } from "@material-ui/core";
import { useDialog, useNavManager, useUserProfile } from "@hooks";
import Fireworks from "react-canvas-confetti/dist/presets/fireworks";
import { CONSULTANT_SLIDER, ConsultantProfileLocation } from "@navigation";
import { MarkHighlighter } from "@common";
import { stringNormalize } from "@bonliva-crm/common";
import { KanbanBoardHeader } from "../kanban-board/kanban-board-header";
import { KanbanBoard } from "../kanban-board/kanban-board";
import { ConsultantPipelineCard } from "./consultant-pipeline-card/consultant-pipeline-card";
import { UntagConsultantDialog } from "./untag-consultant/untag-consultant-dialog";
import { CreateOpportunityFormModal } from "../components/create-opportunity/create-opportunity-form-modal";
import { ConsultantPipelineColumnHeader } from "./consultant-pipeline-column-header";
import { useConsultantPipelineBoardContext } from "./use-consultant-pipeline-board-context";
import { ConsultantPipelineNotFound } from "../components/consultant-pipeline-not-found";
import { SetConsultantRelevantLaterDialog } from "./set-consultant-relevant-later/set-consultant-relevant-later-dialog";
import { FilterSlider } from "@bonliva-crm/ui/search-filters";
import { FormProvider } from "react-hook-form";
import { FavouriteFilter } from "@bonliva-crm/ui/search-filters";
import { SearchProfileType } from "@bonliva-crm/graphql";
import { EditStatusNoteDialog } from "./edit-status-note/edit-status-note-dialog";

export function ConsultantPipelineBoard() {
  const user = useUserProfile();
  const {
    dispatch,
    state,
    loading,
    error,
    filterManager,
    refetch,
    refetchCards,
    setUntagConsultantId,
    setConsultantRelevantLaterId,
    setFireworksConductor,
    setBoardUserId,
    onMoveCard
  } = useConsultantPipelineBoardContext();
  const createOpportunityDialog = useDialog<{ consultantId: string }>();
  const editNoteDialog = useDialog<{ consultantId: string }>();
  const nav = useNavManager();

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (!state && loading) {
    return <div>Loading...</div>;
  }

  if (!state.board) {
    return <ConsultantPipelineNotFound onUserChange={setBoardUserId} />;
  }

  return (
    <FormProvider {...filterManager.formMethods}>
      <Box
        display="flex"
        flexDirection="column"
        flexWrap="wrap"
        flex="1"
        height="90%"
        pl={2}
        pr={2}
        mt={2}
      >
        <CreateOpportunityFormModal
          open={createOpportunityDialog.open}
          consultantId={createOpportunityDialog.data?.consultantId}
          onClose={() => {
            createOpportunityDialog.onClose();
            refetch();
          }}
        />

        <EditStatusNoteDialog
          open={editNoteDialog.open}
          consultantId={editNoteDialog.data?.consultantId}
          onClose={() => {
            editNoteDialog.onClose();
            refetch();
          }}
        />

        <UntagConsultantDialog
          open={!!state.untagConsultantId}
          consultantId={state.untagConsultantId}
          questionText={state.untagConsultantQuestionText}
          onConfirm={() => {
            // User successfully untagged themselves; the consultant no longer
            // belongs on this board, so drop the card.
            const cardId = state.untagConsultantId;
            setUntagConsultantId(null);
            dispatch({
              type: "REMOVE_CARD",
              payload: { cardId }
            });
          }}
          onCancel={() => {
            // CRMDEV-6793: just close the dialog. The card never moved into
            // Ej aktuell in our state (onMoveCard returned early before any
            // MOVE_CARD dispatch), so leaving state.board untouched naturally
            // keeps the card in its source column.
            setUntagConsultantId(null);
          }}
          onError={() => {
            refetch();
          }}
        />

        <SetConsultantRelevantLaterDialog
          open={!!state.setConsultantRelevantLaterId}
          consultantId={state.setConsultantRelevantLaterId}
          onClose={() => {
            refetch();
            setConsultantRelevantLaterId(null);
          }}
        />

        <Box mb={2} flex="0">
          <KanbanBoardHeader
            boardTitle={state.board?.title || ""}
            onUserChange={(userId) => setBoardUserId(userId)}
            onSearch={(v) => filterManager.formMethods.setValue("text", v)}
            onFilterSliderOpen={() => filterManager.setFilterSliderOpen(true)}
            filterIsDirty={state.filterIsDirty}
          />
          <FavouriteFilter
            filterManager={filterManager}
            searchProfileType={SearchProfileType.PipelineBoard}
          />
        </Box>

        <Box
          flex="1"
          display="flex"
          flexDirection="column"
          width="100%"
          style={{ overflow: "hidden" }}
        >
          <Box flex="1" overflow="auto">
            <KanbanBoard
              state={state.board}
              onMoveCard={onMoveCard}
              renderCard={(card, dragging) => (
                <ConsultantPipelineCard
                  key={card.id}
                  card={card}
                  dragging={dragging}
                  canUntagConsultant={state.boardUserId === user.id}
                  onEditStatusNote={() => {
                    editNoteDialog.onOpen({
                      consultantId: card.issue.consultant._id
                    });
                  }}
                  onCreateOpportunity={() => {
                    createOpportunityDialog.onOpen({
                      consultantId: card.issue.consultant._id
                    });
                  }}
                  onOpenProfile={() => {
                    nav.navManager.open(
                      CONSULTANT_SLIDER,
                      new ConsultantProfileLocation(card.issue.consultant._id)
                    );

                    nav.navManager.on("close", CONSULTANT_SLIDER, refetchCards);
                  }}
                  onUntagConsultant={() => {
                    setUntagConsultantId(card.issue.consultant._id);
                  }}
                  onEditNextContactDate={() => {
                    setConsultantRelevantLaterId(card.issue.consultant._id);
                  }}
                />
              )}
              renderColumnHeader={(column) => {
                const initialColumn =
                  state.initialBoard?.columns.find((x) => x.id === column.id) ?? null;
                return (
                  <ConsultantPipelineColumnHeader
                    column={column}
                    initialColumn={initialColumn}
                  />
                );
              }}
            />
          </Box>
        </Box>

        <Fireworks onInit={({ conductor }) => setFireworksConductor(conductor)} />

        <MarkHighlighter
          highlightSearch={[
            filterManager.formMethods.getValues("text"),
            stringNormalize(filterManager.formMethods.getValues("text"))
          ]}
          getMarkjsContext={() => document.querySelectorAll(".hl")}
        />

        <FilterSlider
          filterManager={filterManager}
          searchProfileType={SearchProfileType.PipelineBoard}
        />
      </Box>
    </FormProvider>
  );
}
